'use client'

import { useState } from 'react'

import { trackNewsletterSubscription } from './track-subscription'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type TrackFn = typeof trackNewsletterSubscription

export type NewsletterSignupStatus = 'idle' | 'submitting' | 'success' | 'error'

interface UseNewsletterSignupOptions {
    locale: string
    /** The surface's analytics source (e.g. `landing_c2c`, `blog_home`). */
    subscriptionSource: string
    /** Route context; route-less surfaces default to the `'any'` sentinels. */
    originCity?: string
    destinationCity?: string
    transportMode?: string
    /** Injected in tests; defaults to the real tracking pipeline. */
    trackFn?: TrackFn
}

/**
 * The shared client core of a newsletter signup form: email state, client-side
 * validation, the `idle → submitting → success | error` status machine, and a
 * submit handler that forwards once through the tracking lib with the
 * surface's source and route context. Surfaces (the C2C section, the blog
 * homepage) consume this and own only their presentation — `validationError`
 * is the `'invalid'` sentinel, not user copy; each surface maps it to its own
 * message.
 */
export function useNewsletterSignup({
    locale,
    subscriptionSource,
    originCity = 'any',
    destinationCity = 'any',
    transportMode = 'any',
    trackFn = trackNewsletterSubscription,
}: UseNewsletterSignupOptions) {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<NewsletterSignupStatus>('idle')
    const [validationError, setValidationError] = useState<'invalid' | null>(null)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setValidationError(null)

        if (!EMAIL_RE.test(email)) {
            setValidationError('invalid')
            return
        }

        setStatus('submitting')
        const result = await trackFn({
            email,
            locale,
            originCity,
            destinationCity,
            transportMode,
            subscriptionSource,
        })
        setStatus(result.ok ? 'success' : 'error')
    }

    const isSubscribed = status === 'success'
    const isBusy = status === 'submitting' || isSubscribed

    return { email, setEmail, status, validationError, isBusy, isSubscribed, handleSubmit }
}
